import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { AuthContext } from "@bw/shared/hooks";
import { useContext, useState } from "react";
import { Box, Button, Chip, Link, List, ListItem, Stack, Typography } from "@mui/material";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import type { PublicProgramResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { DateBadge } from "../../../common/components/DateBadge";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { EmptyState, ErrorCard, ListSkeleton } from "../../../common/components/states";
import { useFacilityPrograms } from "../hooks/useFacilityPrograms";
import { useProgramTracks } from "../hooks/useProgramTracks";
import { useMyEnrollments } from "../hooks/useMyEnrollments";
import { TrackAction, TrackRow, joinFacts, trackFacts } from "../components/TrackRow";
import { programScheduleLabel, titleCase } from "../components/classDisplay";
import { usePublicFacility } from "../../booking/hooks/usePublicFacility";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";

type ProgramType = "clinic" | "group" | "camp";
const TYPE_FILTERS: readonly { id: ProgramType | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "clinic", label: "Clinics" },
  { id: "group", label: "Group classes" },
  { id: "camp", label: "Camps" },
];

/**
 * Surface 2 — the PUBLISHED classes at one facility (/classes/:facilityId).
 *
 * One header bar, type chips (a server-side program_type filter), then each
 * class as a date row with its current option rows underneath. Option prices
 * and capacity are each server row's own facts; discovery and options advance
 * only on explicit actions. Signed-in players also see their classes here.
 */
export function FacilityProgramsPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const location = useLocation();
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);
  const user = useContext(AuthContext)?.user ?? null;
  const [type, setType] = useState<ProgramType | "all">("all");
  const programsQ = useFacilityPrograms(facilityId, type === "all" ? undefined : type);
  const facilityQ = usePublicFacility(facilityId);
  const { data, isLoading, isError } = programsQ;

  let body: React.ReactNode;
  if (isLoading) {
    body = <ListSkeleton label="Loading classes…" rows={3} leading="square" />;
  } else if (isError) {
    body = (
      <ErrorCard
        title="Couldn't load classes"
        onRetry={programsQ.restart}
      />
    );
  } else if (!data || data.items.length === 0) {
    body = (
      <EmptyState
        icon={<SchoolRounded />}
        title={type === "all" ? "No classes published yet" : `No ${typeLabel(type)} published yet`}
        body="New classes at this facility will show up here."
        action={
          type === "all"
            ? { label: "Find classes nearby", to: "/play?activity=classes" }
            : { label: "Show all classes", onClick: () => setType("all") }
        }
      />
    );
  } else {
    body = (
      <Stack spacing={1}>
        {data.items.map((program) => (
          <ProgramWithTracks
            key={program.program_id}
            facilityId={facilityId as string}
            program={program}
            loadTracks={Boolean(user)}
            facilitySearchReturn={facilitySearchReturn}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Page narrow>
      <Stack spacing={1.5}>
        <DetailHeader
          back={
            facilityId
              ? {
                  to: withFacilityDiscoveryReturn(
                    `/facility/${encodeURIComponent(facilityId)}`,
                    facilitySearchReturn,
                  ),
                  label: "Back to facility",
                }
              : null
          }
          title="Classes"
          subtitle={facilityQ.data?.name}
        />
        <Box
          role="group"
          aria-label="Class type"
          sx={{ display: "flex", gap: 0.75, overflowX: "auto", scrollbarWidth: "none", mx: { xs: -0.5, sm: 0 }, px: { xs: 0.5, sm: 0 } }}
        >
          {TYPE_FILTERS.map((filter) => (
            <Chip
              key={filter.id}
              label={filter.label}
              clickable
              aria-pressed={type === filter.id}
              color={type === filter.id ? "primary" : "default"}
              variant={type === filter.id ? "filled" : "outlined"}
              onClick={() => setType(filter.id)}
              sx={{ fontWeight: type === filter.id ? 700 : 500, flexShrink: 0 }}
            />
          ))}
        </Box>
        {body}
        {programsQ.canPrevious || programsQ.canNext ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              disabled={!programsQ.ready || !programsQ.canPrevious}
              onClick={programsQ.previous}
            >
              Previous classes
            </Button>
            <Button
              size="small"
              disabled={!programsQ.ready || !programsQ.canNext}
              onClick={programsQ.next}
            >
              Next classes
            </Button>
          </Stack>
        ) : null}
        {user && facilityId ? <MyClassesHere facilityId={facilityId} /> : null}
      </Stack>
    </Page>
  );
}

function typeLabel(type: ProgramType): string {
  return TYPE_FILTERS.find((filter) => filter.id === type)?.label.toLowerCase() ?? "classes";
}

/**
 * One class: its date row (links to detail), then the current page of its
 * options. Prices and capacity come from each server row, never a whole-class
 * summary of a partial page.
 */
function ProgramWithTracks({
  facilityId,
  program,
  loadTracks,
  facilitySearchReturn,
}: {
  facilityId: string;
  program: PublicProgramResponse;
  loadTracks: boolean;
  facilitySearchReturn?: string;
}) {
  const tracksQ = useProgramTracks(program.program_id, loadTracks);
  const detailPath = withFacilityDiscoveryReturn(
    `/classes/${facilityId}/${program.program_id}`,
    facilitySearchReturn,
  );
  // Skip the type when the name already says it ("Junior Clinic").
  const type = titleCase(program.program_type);
  const showType = type.length > 0 && !program.name.toLowerCase().includes(program.program_type.toLowerCase());
  const scheduleFact = programScheduleLabel(program);
  const facts = [showType ? type : null, scheduleFact].filter(Boolean).join(" · ");

  const tracks = tracksQ.data?.items ?? [];
  // A class with exactly one option (and no more pages) is ONE row: its date,
  // the class name (to detail), the option's facts and the option's action.
  const single =
    loadTracks && !tracksQ.isLoading && !tracksQ.isError && tracks.length === 1 && !tracksQ.canNext && !tracksQ.canPrevious
      ? tracks[0]
      : undefined;
  const leading = <DateBadge tile at={program.start_date} timeZone={program.timezone} />;

  if (single) {
    return (
      <Box data-testid="program-card">
        <List disablePadding>
          <ListItem disablePadding data-testid="track-row" sx={{ px: { xs: 0.5, sm: 1 }, py: 0.75, gap: 1.5 }}>
            {leading}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="h2" sx={{ m: 0, fontSize: "0.9375rem", fontWeight: 600, overflowWrap: "anywhere" }}>
                <Link component={RouterLink} to={detailPath} color="inherit" underline="hover">
                  {program.name}
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {joinFacts([
                  <span key="option">{single.name}</span>,
                  ...(scheduleFact ? [<span key="time">{scheduleFact}</span>] : []),
                  ...trackFacts(single),
                ])}
              </Typography>
            </Box>
            <TrackAction
              facilityId={facilityId}
              programId={program.program_id}
              track={single}
              facilitySearchReturn={facilitySearchReturn}
            />
          </ListItem>
        </List>
      </Box>
    );
  }

  return (
    <Box data-testid="program-card">
      <List disablePadding>
        <DenseRow
          to={detailPath}
          leading={leading}
          title={program.name}
          titleComponent="h2"
          subtitle={facts || "Class"}
          trailing={
            <Box
              component="span"
              aria-hidden
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              View
            </Box>
          }
        />
      </List>
      {loadTracks ? (
        <Box sx={{ pl: { xs: 7, sm: 7.5 } }}>
          {tracksQ.isLoading ? (
            <ListSkeleton label="Loading options…" rows={1} leading="none" />
          ) : tracksQ.isError ? (
            <Typography role="status" variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
              Options unavailable right now.
            </Typography>
          ) : tracks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
              {tracksQ.canNext ? "More options remain" : "No options available right now."}
            </Typography>
          ) : (
            <List disablePadding aria-label={`${program.name} options`}>
              {tracks.map((track) => (
                <TrackRow
                  key={track.track_id}
                  facilityId={facilityId}
                  programId={program.program_id}
                  track={track}
                  facilitySearchReturn={facilitySearchReturn}
                />
              ))}
            </List>
          )}
          {tracksQ.canPrevious || tracksQ.canNext ? (
            <Stack direction="row" spacing={1}>
              <Button size="small" disabled={!tracksQ.ready || !tracksQ.canPrevious} onClick={tracksQ.previous}>
                Previous options
              </Button>
              <Button size="small" disabled={!tracksQ.ready || !tracksQ.canNext} onClick={tracksQ.next}>
                Next options
              </Button>
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

/** The signed-in player's own classes at this facility, from their enrollments. */
function MyClassesHere({ facilityId }: { facilityId: string }) {
  const enrollmentsQ = useMyEnrollments();
  const mine = (enrollmentsQ.data ?? []).filter(
    (enrollment) =>
      enrollment.facility_id === facilityId &&
      (enrollment.status === "enrolled" || enrollment.status === "waitlisted" || enrollment.status === "promoted"),
  );
  if (!enrollmentsQ.isSuccess || mine.length === 0) return null;
  return (
    <Stack component="section" aria-labelledby="my-classes-here" spacing={0.5} sx={{ pt: 1 }}>
      <SectionHeader id="my-classes-here" title="My classes" action={{ label: "See all", to: "/me/classes" }} />
      <List disablePadding>
        {mine.map((enrollment) => (
          <DenseRow
            key={enrollment.enrollment_id}
            to={`/classes/${facilityId}/${enrollment.program_id}`}
            leading={
              <Box
                aria-hidden
                sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", flexShrink: 0 }}
              >
                <SchoolRounded fontSize="small" />
              </Box>
            }
            title={enrollment.program_name ?? "Class"}
            subtitle={[enrollment.track_name, titleCase(enrollment.status)].filter(Boolean).join(" · ")}
          />
        ))}
      </List>
    </Stack>
  );
}
